import { SensorStatus } from 'src/app/api/generated';
import { SensorLocalizationSensor } from './sensor-localization.types';

export type SensorLegendSymbol = 'circle' | 'square' | 'triangle' | 'star';

export interface SensorShapeLegendEntry {
  symbol: SensorLegendSymbol;
  symbolLabel: string;
  sensorLabel: string;
}

export interface SensorStatusLegendEntry {
  status: SensorStatus.StatusEnum;
  label: string;
  colors: string[];
}

export interface SensorLegend {
  shapeEntries: SensorShapeLegendEntry[];
  statusEntries: SensorStatusLegendEntry[];
}

const STATUS_DEFINITIONS: Array<{
  status: SensorStatus.StatusEnum;
  label: string;
}> = [
  { status: 'ok', label: 'OK' },
  {
    status: 'stale',
    label: 'Keine aktuellen Daten',
  },
  {
    status: 'warning',
    label: 'Warnung (Stufe 1)',
  },
  {
    status: 'alarm_level_2',
    label: 'Alarm (Stufe 2)',
  },
  {
    status: 'alarm_level_3',
    label: 'Alarm (Stufe 3)',
  },
];

const SYMBOL_LABELS: Record<SensorLegendSymbol, string> = {
  circle: 'Kreis',
  square: 'Quadrat',
  triangle: 'Dreieck',
  star: 'Stern',
};

export function buildSensorLegend(
  sensors: SensorLocalizationSensor[],
  statuses: SensorStatus[] = []
): SensorLegend {
  const statusesBySensorId = new Map(
    statuses.map((status) => [status.sensor_id, status])
  );
  const sensorsBySymbol = new Map<SensorLegendSymbol, Set<string>>();

  for (const sensor of sensors) {
    const symbol = normalizeSymbol(sensor.symbol);
    const sensorLabel = sensor.display_name?.trim() || sensor.influx_name;
    const labels = sensorsBySymbol.get(symbol) || new Set<string>();
    labels.add(sensorLabel);
    sensorsBySymbol.set(symbol, labels);
  }

  const shapeEntries = Array.from(sensorsBySymbol.entries()).map(
    ([symbol, labels]) => ({
      symbol,
      symbolLabel: SYMBOL_LABELS[symbol],
      sensorLabel: Array.from(labels).join(', '),
    })
  );

  const statusEntries = STATUS_DEFINITIONS.map((definition) => {
    const activeColors = sensors
      .map((sensor) => {
        const status = sensor.id
          ? statusesBySensorId.get(sensor.id)
          : undefined;
        if (status?.status !== definition.status) {
          return null;
        }
        return getSensorStatusColor(
          sensor,
          definition.status,
          status.effective_color
        );
      })
      .filter((color): color is string => Boolean(color));

    const configuredColors = sensors.map((sensor) =>
      getSensorStatusColor(sensor, definition.status)
    );

    return {
      status: definition.status,
      label: definition.label,
      colors: uniqueColors(
        activeColors.length > 0 ? activeColors : configuredColors
      ),
    };
  });

  return { shapeEntries, statusEntries };
}

function normalizeSymbol(
  symbol: string | null | undefined
): SensorLegendSymbol {
  switch ((symbol || '').toLowerCase()) {
    case 'square':
      return 'square';
    case 'triangle':
      return 'triangle';
    case 'star':
      return 'star';
    case 'circle':
    default:
      return 'circle';
  }
}

export function getSensorStatusColor(
  sensor: SensorLocalizationSensor,
  status: SensorStatus.StatusEnum,
  effectiveColor?: string | null
): string {
  if (effectiveColor) {
    return effectiveColor;
  }

  switch (status) {
    case 'alarm_level_2':
      return sensor.symbol_color_alarm || '#FF0000';
    case 'alarm_level_3':
      return sensor.symbol_color_alarm_level_3 || '#8B0000';
    case 'warning':
      return sensor.symbol_color_warning || '#FFA500';
    case 'stale':
      return sensor.symbol_color_stale || '#f9a825';
    case 'ok':
    default:
      return sensor.symbol_color_ok || sensor.color || '#1976d2';
  }
}

function uniqueColors(colors: string[]): string[] {
  const unique = new Map<string, string>();
  for (const color of colors) {
    const normalized = color.trim().toLowerCase();
    if (normalized && !unique.has(normalized)) {
      unique.set(normalized, color);
    }
  }
  return Array.from(unique.values());
}
